import React, {Fragment, memo, useCallback, useEffect, useRef, useState} from 'react'
import {Image, Transformer} from 'react-konva'
import {toaster} from '../../../../shared/PFToast'
import {updateToolbarStyle} from '../../context/SelectedVariantContext/helper'
import SpinnerLayer from './SpinnerLayer'
import {actionUpdateImageLayerLoading} from '../../context/SelectedVariantContext/action'

const ImageLayer = memo((props) => {
    const {imageProps, isSelected, onSelect, onChange, zIndexDefault, onChangeSizeImage, background, dispatch} = props
    const {isLock} = imageProps

    const imageRef = useRef(null)
    const trRef = useRef(null)

    const [image, setImage] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(true)
        dispatch(actionUpdateImageLayerLoading(true))
        const img = new window.Image()
        img.crossOrigin = 'Anonymous'
        img.onload = () => {
            const timer = setTimeout(() => {
                setImage(img)
                setLoading(false)
                dispatch(actionUpdateImageLayerLoading(false))
            }, 1000)
            return () => {
                clearTimeout(timer)
            }
        }
        img.onerror = () => {
            setLoading(false)
            toaster({type: 'error', message: 'Error loading image', duration: 6000})
        }
        img.src = imageProps.src
    }, [imageProps.src, dispatch])

    useEffect(() => {
        const imageNode = imageRef.current
        if (imageNode) {
            imageNode.on('mouseenter mouseup dragend', () => {
                imageNode.getStage().container().style.cursor = 'grab'
            })
            imageNode.on('mouseleave', () => {
                imageNode.getStage().container().style.cursor = 'default'
            })
            imageNode.on('mousedown', () => {
                imageNode.getStage().container().style.cursor = 'grabbing'
            })

            return () => {
                imageNode.off('mouseenter')
                imageNode.off('mouseup')
                imageNode.off('dragend')
                imageNode.off('mouseleave')
                imageNode.off('mousedown')
            }
        }
    }, [loading])

    const onShowToolbar = useCallback(() => {
        imageProps.rect = imageRef.current?.getClientRect()
        const trNode = trRef.current
        if (trNode && imageProps) {
            const rotateIcon = trNode.findOne('.rotater')
            const pos = rotateIcon.getAbsolutePosition()
            const {rotation} = imageProps
            const rect = imageRef.current?.getClientRect()
            updateToolbarStyle({...pos, height: rect.height / 2, rotation}, 'image-toolbar')
        }
    }, [imageProps])

    useEffect(() => {
        const imageNode = imageRef.current
        const trNode = trRef.current
        if (imageNode) {
            if (isSelected) {
                trNode.moveToTop()
                trNode.nodes([imageNode])
                !isLock && onShowToolbar()
            } else {
                imageNode.zIndex(zIndexDefault)
                onHideToolbar()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, isLock, zIndexDefault, loading])

    const onItemClick = () => {
        const imageNode = imageRef.current
        if (!imageNode) return

        onSelect({...imageProps})
        setTimeout(() => {
            onShowToolbar()
        }, 100)
    }

    const onDragEnd = (event) => {
        if (!event.target) return

        const attrs = event.target.attrs
        onChange(attrs)
        onShowToolbar()
    }

    const onHideToolbar = () => {
        const toolbar = document.getElementById('image-toolbar')
        toolbar.style.display = 'none'
    }

    const onTransformEnd = (event) => {
        if (!event.target) return
        const imageAttrs = event.target.attrs
        onChange(imageAttrs)
        onShowToolbar()
    }

    const onTransformRealTime = (event) => {
        const imageNode = event.target
        if (!imageNode) return
        const imageAttrs = imageNode.attrs
        if (imageAttrs.dpi === Infinity) return

        const size = {
            width: imageNode.width() || 1,
            height: imageNode.height() || 1,
            scaleX: imageNode.scaleX() || 1,
            scaleY: imageNode.scaleY() || 1,
        }
        onHideToolbar()
        onChangeSizeImage(size)
    }

    const onDragMove = (event) => {
        onHideToolbar()

        const imageNode = event.target
        const trNode = trRef.current
        if (!imageNode && !trNode) return

        const {x: bgX, y: bgY, width: bgWidth, height: bgHeight, ratioDefault} = background
        const {x, y, width, height} = imageNode.getClientRect({relativeTo: trNode})

        const absoluteX = x / ratioDefault
        const absoluteY = y / ratioDefault
        const absoluteWidth = width / ratioDefault
        const absoluteHeight = height / ratioDefault

        if (absoluteX + absoluteWidth < bgX) {
            imageNode.x(bgX - absoluteWidth / 2)
        }

        if (absoluteY + absoluteHeight < bgY) {
            imageNode.y(bgY - absoluteHeight / 2)
        }

        if (absoluteX > bgX + bgWidth) {
            imageNode.x(bgX + bgWidth + absoluteWidth / 2)
        }
        if (absoluteY > bgY + bgHeight) {
            imageNode.y(bgY + bgHeight + absoluteHeight / 2)
        }
    }

    const boundBoxFunc = (oldBox, newBox) => {
        const minSize = Math.min(background.width, background.height) / 100
        if (newBox.width < minSize || newBox.height < minSize) {
            return oldBox
        }
        return newBox
    }

    return (
        <Fragment>
            {loading ? (
                <SpinnerLayer {...imageProps} />
            ) : (
                <Image
                    {...imageProps}
                    image={image}
                    ref={imageRef}
                    listening={!isLock || loading}
                    globalCompositeOperation="source-atop"
                    onClick={onItemClick}
                    onTap={onItemClick}
                    onDragStart={onItemClick}
                    onDragMove={onDragMove}
                    onDragEnd={onDragEnd}
                    onTransformEnd={onTransformEnd}
                />
            )}
            {isSelected && !loading && (
                <Transformer
                    ref={trRef}
                    resizeEnabled={!isLock}
                    rotateEnabled={!isLock}
                    centeredScaling={true}
                    anchorCornerRadius={30}
                    rotateAnchorOffset={20}
                    keepRatio={true}
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                    boundBoxFunc={boundBoxFunc}
                    onTransform={onTransformRealTime}
                    rotateAnchorCursor="grabbing"
                />
            )}
        </Fragment>
    )
})

export default ImageLayer

